import React, {useState} from 'react'
import {CustomModal} from './CustomModal'
import {checkItemName} from './itemName'

export interface ItemNameModalProps {
  title: string
  titleIcon: string
  label: string
  submitText: string
  /** The input's id; Cypress finds the rename field by it. */
  inputId: string
  initialValue?: string
  placeholder?: string
  /** Called with the checked name (trimmed); the caller closes the dialog. */
  onSubmit: (name: string) => void
  onClose: () => void
}

/**
 * Asks for a file or folder name (New folder, Rename). The name is checked as typed
 * ({@link checkItemName}); the dialog stays open with the reason until it can be used.
 */
export const ItemNameModal: React.FC<ItemNameModalProps> = ({
  title,
  titleIcon,
  label,
  submitText,
  inputId,
  initialValue = '',
  placeholder,
  onSubmit,
  onClose,
}) => {
  const [value, setValue] = useState(initialValue)
  // No error before the user has typed or tried to submit: an empty field is the starting point.
  const [showError, setShowError] = useState(false)
  const check = checkItemName(value)
  const errorId = `${inputId}-error`

  const submit = () => {
    if (check.ok) {
      onSubmit(check.name)
    } else {
      setShowError(true)
    }
  }

  const error = showError && !check.ok ? check.error : undefined

  return (
    <CustomModal
      title={title}
      titleIcon={titleIcon}
      closeModal={onClose}
      buttons={[
        {text: 'Cancel', className: 'btn btn-light', onClick: onClose},
        {text: submitText, className: 'btn btn-primary', onClick: submit, disabled: !check.ok},
      ]}
    >
      <>
        <label htmlFor={inputId} className='form-label'>
          {label}
        </label>
        <input
          autoFocus
          className={`form-control${error ? ' is-invalid' : ''}`}
          type='text'
          id={inputId}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setShowError(true)
          }}
          // Enter here, not through a <form>: CustomModal's document-level Enter handler cancels
          // the browser's implicit form submit.
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {error && (
          <div id={errorId} className='invalid-feedback d-block' role='alert'>
            {error}
          </div>
        )}
      </>
    </CustomModal>
  )
}
